import { useState } from "react";
import { propPayoutTax, NOT_INCLUDED } from "@ftr/tax-core";
import { Card, JumpToResult, MoneyField, NotIncluded, Pending, Section, StackBar, WhyPanel } from "../components/ui.tsx";
import { Figure, Line, ResultCard } from "../components/figure.tsx";
import { collect } from "../lib/fields.ts";
import { pct, usd } from "../lib/format.ts";
import type { Profile } from "../lib/profile.ts";
import { AboutYou, PROP_BUSINESS_ASSUMPTION, QbiAnswer, QbiQuestion, baseFrom } from "./shared.tsx";

export function PropPayout({ profile, update }: { profile: Profile; update: (p: Partial<Profile>) => void }) {
  const [gross, setGross] = useState("");
  const [fees, setFees] = useState("");
  const [futures, setFutures] = useState("");

  const c = collect({
    gross: { raw: gross, label: "your payouts" },
    fees: { raw: fees, label: "prop expenses", optional: true },
    futures: { raw: futures, label: "personal-account futures", optional: true, allowNegative: true },
  });
  const b = baseFrom(profile, { net1256: c.values.futures });
  const missing = [...c.missing, ...b.missing];
  const errors = [...c.errors, ...b.errors];
  const ready = missing.length === 0 && errors.length === 0;
  const r = ready ? propPayoutTax(b.base, c.values.gross, c.values.fees) : null;

  return (
    <div className="calc">
      <Card className="inputs">
        <Section label="Your prop year">
          <MoneyField label="Prop payouts for 2026" value={gross} onChange={setGross} hint="Gross, before payout fees. This is what your 1099-NEC shows." />
          <MoneyField label="Prop expenses" optional value={fees} onChange={setFees} hint="Evals, resets, activations, data, platforms, payout fees" />
          <MoneyField label="Personal-account futures gain or (loss)" optional allowNegative value={futures} onChange={setFutures} hint="1099-B Box 11. Use a minus sign for a loss." />
        </Section>
        <AboutYou profile={profile} update={update} />
      </Card>
      {r?.state === "computed" && r.setAsideRate && <JumpToResult text={`Set aside ${pct(r.setAsideRate)} · ${usd(r.taxOnPayouts)}`} />}

      <div className="results">
        {!r && <Pending missing={missing} errors={errors} />}
        {r?.state === "invalid" && <Pending missing={[]} errors={r.errors} />}
        {r?.state === "needs_input" && <QbiQuestion profile={profile} update={update} />}
        {r?.state === "computed" && (
          <ResultCard title="Prop payout tax">
            <p className="result-label">Set aside</p>
            <p className="big">{r.setAsideRate ? <Figure value={r.setAsideRate} kind="pct" places={1} /> : "0%"}</p>
            <p className="result-sub">
              of prop profit for federal tax, about <strong>{usd(r.taxOnPayouts)}</strong> this year.
            </p>
            {r.setAsideRate && (
              <p className="callout">
                Every $1,000 payout → move <strong>{usd(r.setAsideRate.times(1000))}</strong> to tax savings the day it lands.
              </p>
            )}
            <StackBar
              parts={[
                { label: "Self-employment tax", value: r.seTaxPart.toNumber(), tone: "warn" },
                { label: "Income tax", value: r.incomeTaxPart.toNumber(), tone: "ink" },
                { label: "You keep", value: r.takeHome.toNumber(), tone: "good" },
              ]}
            />
            <div className="lines">
              <Line label="Payouts less expenses" note="Schedule C, line 31" value={usd(r.netProfit)} />
              <Line label="Self-employment tax" note="Schedule SE, plus 0.9% Medicare if it applies" value={usd(r.seTaxPart)} tone="warn" />
              <Line label="Extra income tax" note="Income tax this adds (and any NIIT), after the QBI deduction" value={usd(r.incomeTaxPart)} />
              <Line label="Tax on your payouts" value={usd(r.taxOnPayouts)} strong />
              <Line label="You keep" value={usd(r.takeHome)} strong tone="good" />
            </div>
            <div className="lines lines--quiet">
              <Line label="Federal tax estimate for the income entered" value={usd(r.withPayouts.totalTax)} />
              <Line label="Effective rate" value={r.withPayouts.effectiveRate ? pct(r.withPayouts.effectiveRate) : "–"} />
              <Line label="Top income tax bracket" value={pct(r.withPayouts.marginalOrdinaryRate, 0)} />
              {r.withPayouts.qbiDeduction.greaterThan(0) && <Line label="QBI deduction used" value={usd(r.withPayouts.qbiDeduction)} />}
              {r.withPayouts.capitalLossCarryforward.greaterThan(0) && (
                <Line
                  label="Futures loss carried to 2027"
                  note={`${usd(r.withPayouts.shortTermCarryforward)} short-term, ${usd(r.withPayouts.longTermCarryforward)} long-term`}
                  value={usd(r.withPayouts.capitalLossCarryforward)}
                />
              )}
            </div>
            {r.netProfit.lessThan(0) && (
              <p className="callout callout--warn">Expenses are more than payouts. That loss can lower your other taxes, but repeated losing years can draw IRS questions about whether this is a business. Keep records.</p>
            )}
            <WhyPanel trace={r.withPayouts.trace} />
            <QbiAnswer profile={profile} update={update} />
            <NotIncluded items={NOT_INCLUDED} assumedZero={[...c.assumedZero, ...b.assumedZero]} assumptions={[PROP_BUSINESS_ASSUMPTION, ...b.assumptions]} />
          </ResultCard>
        )}
      </div>
    </div>
  );
}
